import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { WheelPicker, WheelPickerWrapper } from '../ui/wheel-picker';
import type { WheelPickerOption, WheelPickerValue } from '../ui/wheel-picker';

interface WheelPickerSheetProps<T extends WheelPickerValue> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Heads the sheet. */
  label: string;
  value: T | null;
  options: WheelPickerOption<T>[];
  onChange: (value: T) => void;
  /** Rendered under the wheel, for what the sheet decides beyond the value. */
  extra?: ReactNode;
}

/**
 * A bottom sheet holding one iOS-style wheel, opened by whatever the caller
 * decides — a pill of its own (see WheelPickerField) or the very button that
 * chose to need a number.
 *
 * The wheel is a draft until it is confirmed: it opens on the current value (or
 * the first option, when there is none yet), and only OK writes it back —
 * merely opening the sheet must not grade a problem, and dismissing it leaves
 * the value where it was.
 */
export default function WheelPickerSheet<T extends WheelPickerValue>({
  open,
  onOpenChange,
  label,
  value,
  options,
  onChange,
  extra,
}: WheelPickerSheetProps<T>) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<T | null>(value);

  // Every opening starts a fresh draft off what is currently saved. Done while
  // rendering rather than from an effect so the wheel never shows, even for a
  // frame, the value it was left on the last time it was open.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(value ?? options[0]?.value ?? null);
  }

  const confirm = () => {
    if (draft != null) onChange(draft);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* The wheel is the whole sheet, so its heading says all there is to
          say — Radix asks for the description to be waived out loud. */}
      <SheetContent side="bottom" className="pb-8" aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle>{label}</SheetTitle>
        </SheetHeader>
        <div className="flex justify-center pt-4">
          {draft != null && (
            <WheelPickerWrapper>
              <WheelPicker value={draft} onValueChange={setDraft} options={options} />
            </WheelPickerWrapper>
          )}
        </div>
        {extra && <div className="pt-4">{extra}</div>}
        <SheetFooter className="pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={confirm}>
            {t('common.ok')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
